import React from 'react';
import PropTypes, { InferProps } from 'prop-types';
import styled from '@emotion/styled';
import Premium from '../Icons/Premium';
import Headline5 from '../Typography/Heading/Headline5';
import SubTitle from '../Typography/SubTitle';
import Body2 from '../Typography/Body/Body2';
import { ListItem } from './List';
import Skeleton from '../SkeletonLoader/Skeleton';
import colors from '../Theme/colors';

const ListItemContent = styled.div`
  display: flex;
  margin: 0;
  padding: 24px 0;
  min-height: 72px;
`;

const BrandListItem = styled(ListItem)<{ inactive?: boolean }>`
  border-bottom: 1px solid ${({ theme }) => theme.colors.greyLight};
  margin: 0;
  padding: 0 28px;

  cursor: ${({ inactive }) => (
    inactive ? 'default;' : 'pointer'
  )};
  h2 {
    color: ${({ inactive }) => (inactive
      ? colors.greyLight
      : colors.black
    )}
  }
  a {
    width: 100%;
  }
`;

const PremiumIconWrapper = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
`;

const EditIconWrapper = styled(PremiumIconWrapper)`
  font-family: ${({ theme }) => theme.fonts.primary};

  a {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    > svg {
      outline: none;
      margin: 0 0 8px;
    }
  }
`;

const LogoContainer = styled.div`
  padding-right: 24px;
  width: 60px;
  display: flex;
  align-items: center;
`;

const DetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const StyledSubTitle = styled(SubTitle)`
  && {
    font-family: ${({ theme }) => theme.fonts.primary};
    line-height: 1.5rem;
  }
`;

const StyledBody2 = styled(Body2)`
  &&& {
    margin: 2px 0;
    max-width: 90ch;
    overflow-wrap: break-word;
  }
`;

const brandListItemContentPropertyTypes = {
  title: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
  ]),
  logo: PropTypes.node,
  premium: PropTypes.bool,
  edit: PropTypes.node,
  industries: PropTypes.node,
  country: PropTypes.string,
  description: PropTypes.node,
  showLoader: PropTypes.bool,
};

type BrandListItemContentProperties = InferProps<
  typeof brandListItemContentPropertyTypes
>;

const BrandListItemContent = ({
  logo,
  title,
  premium,
  edit,
  industries,
  country,
  description,
  showLoader,
}: BrandListItemContentProperties) => (
  <ListItemContent>
    <LogoContainer>
      {showLoader ? (
        <Skeleton variant="rectangular" width="60px" height="60px">
          {logo}
        </Skeleton>
      ) : logo}
    </LogoContainer>
    <DetailsContainer>
      <Headline5 tag="h2">
        {showLoader ? (
          <Skeleton variant="rectangular" width="200px">&nbsp;</Skeleton>
        ) : title}
      </Headline5>
      { industries
      && (
      <StyledBody2 tag="div">
        {industries}
      </StyledBody2>
      ) }
      { description && (
      <StyledBody2 tag="span">
        { showLoader ? (
          <Skeleton variant="rectangular" width="100%" height="60px">
            &nbsp;
          </Skeleton>
        ) : description }
      </StyledBody2>
      )}
      { country && (
      <StyledSubTitle tag="span">
        { country }
      </StyledSubTitle>
      )}
    </DetailsContainer>
    {(premium ?? false) && (
      <PremiumIconWrapper>
        <Premium fillColor={colors.primary} />
      </PremiumIconWrapper>
    )}
    {edit}
  </ListItemContent>
);

BrandListItemContent.propTypes = brandListItemContentPropertyTypes;

export {
  BrandListItem,
  BrandListItemContent,
  EditIconWrapper,
};
